import { API } from '@/redux/config/index';
import { toast } from 'react-toastify';
import { raiseErrorException } from '../../utils/raiseErrorException';
import {
  start,
  setReducerError,
  setAddAlertRecipients,
  setAddedNotificationType,
  setAlertingPolicies,
  setAlertingPolicy,
  setAlertingPolicyByName,
  setAlertingTool,
  setAlertingTools,
  setCustomerAlertRecipients,
  setDeletedPolicyNotification,
  setMappingDetails,
  setNewAlertingPolicies,
  setNewToolMappings,
  setNotificationSettings,
  setToolDetails,
  setToolMappings,
  setTools,
  setUpdateAlertRecipients,
  setCustomerToolPolicies,
  setDisableCustomersDefaultPolicy,
  setEnableCustomersDefaultPolicy,
  setUserProvisioningData,
  setAddUserProvisioningData,
} from '../reducers/alertingToolsReducer';

// Get Alerting Tools
export const getAlertingTools = () => async (dispatch) => {
  try {
    dispatch(start());
    const response = await API.get('/alerting_tools/tool');

    dispatch(setAlertingTools(response?.data));
  } catch (err) {
    raiseErrorException(err, dispatch, setReducerError, 'get');
  }
};

// Create Alerting Tools
export const createAlertingTool = (name) => async (dispatch) => {
  try {
    dispatch(start());
    const response = await API.post('/alerting_tools/tool', name);

    dispatch(setTools(response?.data));
    toast.success('Alerting Tool Created Successfully');
  } catch (err) {
    raiseErrorException(err, dispatch, setReducerError, 'post');
  }
};

// Get Alerting Tools With ID
export const getAlertingToolByID = (id) => async (dispatch) => {
  try {
    dispatch(start());
    const response = await API.get(`/alerting_tools/tool/${id}`);

    dispatch(setAlertingTool(response?.data));
  } catch (err) {
    raiseErrorException(err, dispatch, setReducerError, 'get');
  }
};

// Get alerting tool Mapping with name
export const getAlertingToolMappingWithName = (name) => async (dispatch) => {
  try {
    dispatch(start());
    const response = await API.get(`/alerting_tools/tool/${name}/mapping`);

    dispatch(setToolDetails(response?.data));
  } catch (err) {
    raiseErrorException(err, dispatch, setReducerError, 'get');
  }
};

// Get alerting tool Mappings
export const getAlertingToolMappings = (customerID, toolID) => async (dispatch) => {
  try {
    dispatch(start());
    const response = await API.get(`/alerting_tools/tool_mapping?filterCustomer=true&toolID=${toolID}`);

    dispatch(setToolMappings(response?.data));
  } catch (err) {
    raiseErrorException(err, dispatch, setReducerError, 'get');
  }
};

// Create alerting tool Mapping
export const createAlertingToolMapping = (customerID, toolID, tenantID) => async (dispatch) => {
  try {
    dispatch(start());
    const response = await API.post('/alerting_tools/tool_mapping', {
      customerID,
      toolID,
      tenantID,
    });

    dispatch(setNewToolMappings(response?.data));
    toast.success('Alerting Tool Mapping Created Successfully');
  } catch (err) {
    raiseErrorException(err, dispatch, setReducerError, 'post');
  }
};

// get alerting tool Mapping with ID
export const getAlertingToolMappingWithID = (id) => async (dispatch) => {
  try {
    dispatch(start());
    const response = await API.get(`/alerting_tools/tool_mapping/${id}`);

    dispatch(setMappingDetails(response?.data));
  } catch (err) {
    raiseErrorException(err, dispatch, setReducerError, 'get');
  }
};

// Delete Alerting tool Mapping with ID
export const deleteAlertingToolMappingWithID = (id) => async (dispatch) => {
  try {
    dispatch(start());
    const response = await API.delete(`/alerting_tools/tool_mapping/${id}`);

    dispatch(setToolMappings(response?.data));
    toast.success('Alerting Tool Mapping Deleted Successfully');
  } catch (err) {
    raiseErrorException(err, dispatch, setReducerError, 'delete');
  }
};

// get alerting tool policies
export const getAlertingToolPolicies = () => async (dispatch) => {
  try {
    dispatch(start());
    const response = await API.get(`/alerting_tools/tool_policy`);

    dispatch(setAlertingPolicies(response?.data));
  } catch (err) {
    raiseErrorException(err, dispatch, setReducerError, 'get');
  }
};

// get alerting tool policy with ID
export const getAlertingToolPolicyWithID = (id) => async (dispatch) => {
  try {
    dispatch(start());
    const response = await API.get(`/alerting_tools/tool_policy/${id}`);

    dispatch(setAlertingPolicy(response?.data));
  } catch (err) {
    raiseErrorException(err, dispatch, setReducerError, 'get');
  }
};

// create alerting tool policy
export const createAlertingToolPolicy =
  (toolID, name, config, conditions, field, operator, value) => async (dispatch) => {
    try {
      dispatch(start());
      const response = await API.post('/alerting_tools/tool_policy', {
        name,
        toolID,
        config: {
          conditions: [
            {
              field,
              operator,
              value,
            },
          ],
        },
      });

      dispatch(setNewAlertingPolicies(response?.data));
      toast.success('Alerting Tool Policy Created Successfully');
    } catch (err) {
      raiseErrorException(err, dispatch, setReducerError, 'post');
    }
  };

// delete alerting tool policy with ID
export const deleteAlertingToolPolicyWithID = (id) => async (dispatch) => {
  try {
    dispatch(start());
    const response = await API.delete(`/alerting_tools/tool_policy/${id}`);

    dispatch(setAlertingPolicies(response?.data));
    toast.success('Alerting Tool Policy Deleted Successfully');
  } catch (err) {
    raiseErrorException(err, dispatch, setReducerError, 'delete');
  }
};

// get alerting tool policy by name
export const getAlertingToolPolicyByName = (name) => async (dispatch) => {
  try {
    dispatch(start());
    const response = await API.get(`/alerting_tools/tool_policy/name/${name}`);

    dispatch(setAlertingPolicyByName(response?.data));
  } catch (err) {
    raiseErrorException(err, dispatch, setReducerError, 'get');
  }
};

// get notification settings for tool policy
export const getNotificationSettingsForToolPolicy = (id) => async (dispatch) => {
  try {
    dispatch(start());
    const response = await API.get(`/alerting_tools/tool_policy/${id}/notification_settings`);

    dispatch(setNotificationSettings(response?.data));
  } catch (err) {
    raiseErrorException(err, dispatch, setReducerError, 'get');
  }
};

// add notification type for alert policy
export const addNotificationTypeForAlertPolicy = (policyID, notificationType) => async (dispatch) => {
  try {
    dispatch(start());
    const response = await API.post('/alerting_tools/tool_policy/notification', {
      policyID,
      notificationType,
    });
    dispatch(setAddedNotificationType(response?.data));
    toast.success('Notification Type Added Successfully');
  } catch (err) {
    raiseErrorException(err, dispatch, setReducerError, 'post');
  }
};

// delete alert policy notification setting by ID
export const deleteAlertPolicyNotificationSettingByID = (id, notificationID) => async (dispatch) => {
  try {
    dispatch(start());
    const response = await API.delete(`/alerting_tools/tool_policy/${id}/notification/${notificationID}`);

    dispatch(setDeletedPolicyNotification(response?.data));
  } catch (err) {
    raiseErrorException(err, dispatch, setReducerError, 'delete');
  }
};

// add email participants for a customer
export const addEmailParticipantsForCustomer = (customerID, email) => async (dispatch) => {
  try {
    dispatch(start());
    const response = await API.post('/alerting_tools/email_recipients', {
      customerID,
      email,
    });

    dispatch(setAddAlertRecipients(response?.data));
    toast.success('Email Participants Added Successfully');
  } catch (err) {
    raiseErrorException(err, dispatch, setReducerError, 'post');
  }
};

// get alerting email recipients by customerID
export const getAlertingEmailRecipientsByCustomerID = (id) => async (dispatch) => {
  try {
    dispatch(start());
    const response = await API.get(`/alerting_tools/email_recepients/customer/${id}`);

    dispatch(setCustomerAlertRecipients(response?.data));
  } catch (err) {
    raiseErrorException(err, dispatch, setReducerError, 'get');
  }
};

// update email participants for for a customer
export const updateEmailParticipantsForCustomer = (customerID, email) => async (dispatch) => {
  try {
    dispatch(start());
    const response = await API.put('/alerting_tools/email_recipients', {
      customerID,
      email,
    });

    dispatch(setUpdateAlertRecipients(response?.data));
    toast.success('Email Participants Updated Successfully');
  } catch (err) {
    raiseErrorException(err, dispatch, setReducerError, 'put');
  }
};

// get customer Tool Policys
export const getCustomerToolPolicies = (customerID, tool, setIsResponse) => async (dispatch) => {
  try {
    dispatch(start());
    const response = await API.get(`/alerting_tools/tool_policy/customer`);
    dispatch(setCustomerToolPolicies(response?.data));
    setIsResponse(true);
  } catch (err) {
    setIsResponse(false);
    raiseErrorException(err, dispatch, setReducerError, 'get');
  }
};

// add customer to tool default tool policy
export const addCustomerToToolDefaultToolPolicy = (data) => async (dispatch) => {
  try {
    dispatch(start());
    const response = await API.post('/alerting_tools/enable/default', data);
    if (response.status === 200) {
      toast.success('Customer Successfully Enabled Alert System');
    }
    dispatch(setEnableCustomersDefaultPolicy(response?.data));
  } catch (err) {
    const errMsg = err?.response?.data.detail[0]?.msg || err?.response?.data.detail;
    raiseErrorException(err, dispatch, setReducerError, 'post');
  }
};

//# remove customer from the the tools default tool policy
export const removeCustomerFromToolDefaultToolPolicy = (data) => async (dispatch) => {
  try {
    dispatch(start());
    const response = await API.post('/alerting_tools/disable/default', data);
    toast.success('Customer Successfully Removed From Alert System');
    dispatch(setDisableCustomersDefaultPolicy(response?.data));
  } catch (err) {
    const errMsg = err?.response?.data.detail[0]?.msg || err?.response?.data.detail;
    raiseErrorException(err, dispatch, setReducerError, 'post');
  }
};

//# get USer Provisioning
export const getUserProvisioning = (customerID) => async (dispatch) => {
  try {
    dispatch(start());
    const response = await API.get(`/customers/scim_url`);
    dispatch(setUserProvisioningData(response?.data));
  } catch (err) {
    if (err.response && err.response.status !== 404) {
      toast.error(err?.response?.data?.detail);
    }
    dispatch(setUserProvisioningData(''));
    raiseErrorException(err, dispatch, setReducerError, 'get');
  }
};

//# add Provisioning
export const addUserProvisioning = (data) => async (dispatch) => {
  try {
    dispatch(start());
    const response = await API.post('/alerting_tools/disable/default', data);
    toast.success('User Provisioning Added Successfully');
    dispatch(setAddUserProvisioningData(response?.data));
  } catch (err) {
    raiseErrorException(err, dispatch, setReducerError, 'post');
  }
};
